/**
 * @file schemas.ts
 * @brief Zod schemas for Super Admin Reports API responses.
 *
 * @details
 * Phase A P0: revenue-by-product + company-health. Cost-margin reuses
 * analytics revenue schemas (no dedicated report endpoint).
 *
 * @references
 * - BACKEND/src/features/admin/service.js (getRevenueByProductReport,
 *   getCompanyHealthReport)
 *
 * @author DES
 * @date 27-07-2026
 */

import { z } from "zod";

const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
  z.null().transform(() => 0),
]);

export type ReportRange = {
  startDate?: string;
  endDate?: string;
  companyId?: string;
  groupBy?: "day" | "week" | "month";
  idleDays?: number;
};

export const RevenueByProductRowSchema = z
  .object({
    product: z.string(),
    product_label: z.string(),
    // (24-08-2026) The platform feed spans tenants and therefore currencies;
    // rows are now per (product, currency).
    currency: z.string().nullable().optional(),
    invoice_count: numericString,
    revenue_cents: numericString,
    cost_cents: numericString,
    profit_cents: numericString,
  })
  .passthrough();
export const RevenueByProductSchema = z.array(RevenueByProductRowSchema);
export type RevenueByProductRow = z.infer<typeof RevenueByProductRowSchema>;

export const CompanyHealthRowSchema = z
  .object({
    company_id: z.string(),
    company_name: z.string().nullable().optional(),
    company_status: z.string().nullable().optional(),
    session_count: numericString,
    total_duration_seconds: numericString,
    total_cost_cents: numericString,
    last_session_at: z.string().nullable().optional(),
    is_idle: z.boolean(),
    days_since_last_session: z
      .union([z.number(), z.null()])
      .optional()
      .transform((v) => (v == null ? null : Number(v))),
  })
  .passthrough();
export const CompanyHealthSchema = z.array(CompanyHealthRowSchema);
export type CompanyHealthRow = z.infer<typeof CompanyHealthRowSchema>;

export const BillingAnomalyRowSchema = z
  .object({
    kind: z.string(),
    product: z.string().nullable().optional(),
    product_label: z.string().nullable().optional(),
    ref_id: z.string().nullable().optional(),
    owner_email: z.string().nullable().optional(),
    company_id: z.string().nullable().optional(),
    company_name: z.string().nullable().optional(),
    amount_cents: numericString,
    currency: z.string().nullable().optional(),
    occurred_at: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
  })
  .passthrough();

export const BillingAnomaliesReportSchema = z
  .object({
    summary: z
      .object({
        payment_failed: numericString,
        unpaid_invoice: numericString,
        incomplete_subscription: numericString,
        pool_overage: numericString,
        total: numericString,
      })
      .passthrough(),
    anomalies: z.array(BillingAnomalyRowSchema),
  })
  .passthrough();
export type BillingAnomaliesReport = z.infer<typeof BillingAnomaliesReportSchema>;
export type BillingAnomalyRow = z.infer<typeof BillingAnomalyRowSchema>;

export const EnterprisePipelineReportSchema = z
  .object({
    summary: z
      .object({
        total_inquiries: numericString,
        seats_requested: numericString,
        by_status: z.record(z.string(), numericString).optional(),
        by_intent: z.record(z.string(), numericString).optional(),
        active_plans: numericString,
        seats_sold: numericString,
        seats_assigned: numericString,
        seats_unassigned: numericString,
      })
      .passthrough(),
    inquiries: z.array(
      z
        .object({
          id: z.string(),
          status: z.string(),
          intent: z.string().optional(),
          contact_name: z.string().nullable().optional(),
          contact_email: z.string().nullable().optional(),
          seats_required: numericString,
          created_at: z.string().nullable().optional(),
          company_name: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
    plans: z.array(
      z
        .object({
          company_id: z.string(),
          company_name: z.string().nullable().optional(),
          status: z.string(),
          seat_limit: numericString,
          seats_assigned: numericString,
          included_minutes: numericString,
          period_start: z.string().nullable().optional(),
          period_end: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type EnterprisePipelineReport = z.infer<
  typeof EnterprisePipelineReportSchema
>;

export const QualityOpsReportSchema = z
  .object({
    summary: z
      .object({
        meetings_error: numericString,
        meetings_stuck: numericString,
        total_issues: numericString,
      })
      .passthrough(),
    cost_status: z
      .object({
        enabled: z.boolean().optional(),
        threshold: numericString.optional(),
        current_cost: numericString.optional(),
        // (24-08-2026) AI vendor COGS, kept in USD-cents by product convention
        // (pricing-model.md §1) — the panel used to print it as GBP.
        currency: z.string().optional(),
        percent_used: numericString.optional(),
        is_warning: z.boolean().optional(),
        is_critical: z.boolean().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    meetings: z.array(
      z
        .object({
          id: z.string(),
          title: z.string().nullable().optional(),
          status: z.string().nullable().optional(),
          started_at: z.string().nullable().optional(),
          updated_at: z.string().nullable().optional(),
          owner_email: z.string().nullable().optional(),
          company_name: z.string().nullable().optional(),
          total_duration_seconds: numericString,
          issue: z.string().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type QualityOpsReport = z.infer<typeof QualityOpsReportSchema>;
